import { useMutation } from '@tanstack/react-query';
import { Modal, Button, message, Select, Input } from 'antd';
import { useMemo, useState } from 'react';
import HeadingTypography from 'src/app/components/shared/Typography/Heading';
import TextTypography from 'src/app/components/shared/Typography/Text';
import { acceptOrRejectLead, holdLead } from 'src/app/services/lead';
import { ApprovedRejectCreditStatusEnum, LeadCreditHoldStatusEnum } from '@rasayi-workspace/shared';
import { GetUserDetails } from '@services';

interface IViewPlanModal {
    modalType: 'hold' | 'accept' | 'reject';
    visible: string;
    onClose: () => void;
    refetch: any;
}
const { Option } = Select;
const heading = {
    hold: {
        heading: 'Hold Request',
        subHeadding: 'Please add a note to help the agent before holding the request.',
        savetext: 'Yes, Move to Hold',
    },
    accept: {
        heading: 'Approval Confirmation',
        subHeadding: 'Please enter the EMI details and add a comment before approving the request.',
        savetext: 'Approved Request',
    },
    reject: {
        heading: 'Declining Request',
        subHeadding: 'Please add a note before declining/rejecting the request.',
        savetext: 'Yes, Decline',
    },
};

export const HoldModal = ({ visible, onClose, refetch, modalType }: IViewPlanModal) => {
    const [messageApi, contextHolder] = message.useMessage();
    const [status, setStatus] = useState('');
    const [comment, setComment] = useState('');
    const [emi, setEmi] = useState('');
    const clearHandler = () => {
        onClose();
        setStatus('');
        setComment('');
        setEmi('');
    };

    const { mutate: UpdateLeadHandler, isLoading: isHoldLoading } = useMutation<any>({
        mutationKey: ['updateLead'],
        mutationFn: async () => {
            const user = GetUserDetails();
            const payload: {
                id: string;
                emi?: string;
                status?: string;
                isHold?: boolean;
                comment?: string;
                rejectionReason?: string;
            } = {
                id: visible,
            };
            if (modalType === 'hold') {
                payload.isHold = true;
                payload.emi = emi;
                payload.status = status;
                payload.comment = comment;
                return holdLead(payload);
            } else if (modalType == 'accept') {
                // { emi, status, rejectionReason, comment }
                payload.emi = emi;
                payload.status = ApprovedRejectCreditStatusEnum.APPROVED;
                payload.comment = comment;
            } else if (modalType == 'reject') {
                payload.status = ApprovedRejectCreditStatusEnum.REJECTED;
                payload.rejectionReason = comment;
            }

            return acceptOrRejectLead(payload);
        },
        onSuccess: () => {
            setTimeout(() => {
                refetch();
                clearHandler();
            }, 1000);
            messageApi.success(`Lead ${modalType} successfully!`);
        },
        onError: (e: any) => {
            messageApi.error(
                <div>
                    {e?.response?.data?.message?.map((msg: string, index: number) => (
                        <div className="text-start	" key={index}>
                            {msg}
                        </div>
                    )) || `Failed to ${modalType} lead. Contact support.`}
                </div>
            );
        },
    });

    const disabledConditions = useMemo(() => {
        if (modalType === 'hold') return comment && status && emi;
        if (modalType === 'accept') return comment && emi;
        return comment;
    }, [emi, status, comment, modalType]);

    return (
        <Modal open={visible ? true : false} onCancel={clearHandler} footer={null} width={700} className="p-6">
            {contextHolder}
            <div className="text-center mb-4">
                <HeadingTypography classNames=" font-semibold text-[#2E2A2A]" fontSize={22}>
                    {heading[modalType].heading}
                </HeadingTypography>
            </div>
            <div className="flex justify-center">
                <TextTypography fontSize={14}>{heading[modalType].subHeadding}</TextTypography>
            </div>
            <div className="flex flex-col justify-center items-center">
                {modalType !== 'reject' ? (
                    <div className="flex mt-5 gap-[10px]">
                        {modalType === 'hold' ? (
                            <Select
                                className={`custom-select-placeholder  h-[36px] w-[300px] !text-[#007AFF] !bg-white text-[12px] lead-credit-selector`}
                                allowClear
                                placeholder="Choose Reason/Status"
                                value={status || null}
                                onChange={val => {
                                    setStatus(val);
                                }}
                            >
                                {Object.values(LeadCreditHoldStatusEnum).map(_status => {
                                    return <Option value={_status}>{_status}</Option>;
                                })}
                            </Select>
                        ) : null}

                        <Input
                            className={`h-[40px]  ${modalType === 'hold' ? ' w-[200px]' : ' w-[510px]'} border border-[#DBD7D7] hover:!border-[#DBD7D7] focus:!border-[#DBD7D7] shadow-none`}
                            placeholder="Please Enter EMI"
                            onChange={e => setEmi(e.target.value)}
                        />
                    </div>
                ) : null}

                <div className="mt-5 ">
                    <Input.TextArea
                        onChange={e => {
                            setComment(e.target.value);
                        }}
                        placeholder="Please Provide a Note"
                        rows={3}
                        className="mb-2 w-[510px]"
                    />
                </div>
            </div>

            <div className="flex justify-center mt-4 gap-2">
                <Button onClick={clearHandler} className="mr-2 text-red-700 border-red-700 bg-white hover:bg-red-700 !hover:text-white !hover:border-white transition-colors">
                    No, Cancel
                </Button>

                <Button
                    onClick={() => {
                        UpdateLeadHandler();
                    }}
                    disabled={isHoldLoading || !disabledConditions}
                    loading={isHoldLoading}
                    className={`bg-red-700 text-white border-red-700 hover:bg-red-800 hover:border-red-800 hover:text-white !text-white transition-colors ${
                        !disabledConditions && 'bg-[#FCEFF0] border-0 hover:bg-[#FCEFF0] hover:border-0'
                    }`}
                >
                    {heading[modalType].savetext}
                </Button>
            </div>
        </Modal>
    );
};
